import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiShield, FiClock, FiCheckCircle, FiXCircle, FiCopy, FiRefreshCw, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { API_URL } from '../../../utils/authUtils';

/**
 * ConsentPanel - Handles AWAITING status state.
 *
 * Allows staff to:
 * - Create consent requests with requester teaser and privacy level
 * - Track pending consent status with expiry countdown
 * - Record manual consent responses (grant/decline/expire)
 * - Auto-generate requester teaser via teaser API
 */

const ConsentPanel = ({ requestDetail, requestId, onRequestUpdated, authHeaders }) => {
  const [consentData, setConsentData] = useState(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Create consent form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [privacyLevel, setPrivacyLevel] = useState(2);
  const [requesterTeaser, setRequesterTeaser] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [teaserLoading, setTeaserLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // Response recording
  const [respondLoading, setRespondLoading] = useState(false);
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [declinedReason, setDeclinedReason] = useState('');

  // Transition
  const [transitionLoading, setTransitionLoading] = useState(false);

  // Test mode
  const [testMode, setTestMode] = useState(false);

  // Request reference
  const [showRequestRef, setShowRequestRef] = useState(false);

  const getErrorMessage = (err, defaultMsg) => {
    if (err.response?.data) {
      const detail = err.response.data.detail;
      if (typeof detail === 'string') return detail;
      if (detail?.message) return detail.message;
      if (detail?.error) return detail.error;
    }
    return defaultMsg;
  };

  const fetchConsentDetails = async () => {
    setConsentLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/consent`,
        authHeaders()
      );
      setConsentData(res.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        setError(getErrorMessage(err, 'Failed to load consent details'));
      }
    } finally {
      setConsentLoading(false);
    }
  };

  useEffect(() => {
    fetchConsentDetails();
  }, [requestId]);

  const generateTeaser = async () => {
    if (!requestDetail.requester_person_id) {
      setError('No requester linked to this request');
      return;
    }
    setTeaserLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/staff/brokerage/executives/${requestDetail.requester_person_id}/teaser?privacy_level=${privacyLevel}`,
        authHeaders()
      );
      setRequesterTeaser(res.data.teaser || '');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to generate teaser'));
    } finally {
      setTeaserLoading(false);
    }
  };

  const createConsentRequest = async () => {
    if (!requesterTeaser.trim()) {
      setError('Please provide a requester teaser');
      return;
    }
    setCreateLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/consent-request`,
        {
          requester_teaser: requesterTeaser,
          privacy_level: privacyLevel,
          custom_message: customMessage || null,
          test_mode: testMode,
        },
        authHeaders()
      );
      const data = res.data;
      if (data.email_sent) {
        setSuccessMsg(`Consent request created and email sent to ${data.email_recipient}`);
      } else if (data.email_error) {
        setSuccessMsg(`Consent request created, but email could not be sent: ${data.email_error}`);
      } else {
        setSuccessMsg('Consent request created successfully');
      }
      setShowCreateForm(false);
      setRequesterTeaser('');
      setCustomMessage('');
      onRequestUpdated();
      fetchConsentDetails();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('A consent request already exists for this request');
        fetchConsentDetails();
      } else {
        setError(getErrorMessage(err, 'Failed to create consent request'));
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const recordConsentResponse = async (status) => {
    if (status === 'declined' && !declinedReason.trim()) {
      setError('Please provide a reason for declining');
      return;
    }

    const latestConsent = consentData?.items?.[0];
    if (!latestConsent?.id) {
      setError('No consent record found');
      return;
    }

    setRespondLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/consents/${latestConsent.id}/respond`,
        {
          status,
          declined_reason: status === 'declined' ? declinedReason : null,
        },
        authHeaders()
      );
      setSuccessMsg(`Consent ${status} recorded successfully`);
      setShowDeclineReason(false);
      setDeclinedReason('');
      onRequestUpdated();
      fetchConsentDetails();
    } catch (err) {
      setError(getErrorMessage(err, `Failed to record ${status} response`));
    } finally {
      setRespondLoading(false);
    }
  };

  const transitionRequest = async (targetStatus) => {
    setTransitionLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/transition`,
        {
          target_status: targetStatus,
          row_version: requestDetail.row_version,
        },
        authHeaders()
      );
      onRequestUpdated();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Conflict: Modified by another user. Refreshing...');
        onRequestUpdated();
      } else {
        setError(getErrorMessage(err, `Failed to transition to ${targetStatus}`));
      }
    } finally {
      setTransitionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccessMsg('Copied to clipboard');
      setTimeout(() => setSuccessMsg(null), 2000);
    });
  };

  const getExpiryInfo = (consent) => {
    if (!consent?.consent_expires_at) return null;
    const expires = new Date(consent.consent_expires_at);
    const now = new Date();
    const diffMs = expires - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: 'Expired', color: 'text-red-600', urgent: true };
    if (diffDays <= 2) return { text: `${diffDays}d remaining`, color: 'text-orange-600', urgent: true };
    return { text: `${diffDays}d remaining`, color: 'text-gray-600', urgent: false };
  };

  const latestConsent = consentData?.items?.[0];
  const hasActiveConsent = latestConsent && latestConsent.consent_status === 'pending';
  const consentGranted = latestConsent && latestConsent.consent_status === 'granted';

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Consent Management</h3>
        <p className="text-xs text-gray-500">
          Request and track consent from the target before proceeding with the introduction.
        </p>
      </div>

      {/* Original Request Reference */}
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        <button
          onClick={() => setShowRequestRef(!showRequestRef)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          <span>Original Request Details</span>
          {showRequestRef ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
        </button>
        {showRequestRef && (
          <div className="px-3 pb-3 space-y-2 text-xs border-t border-gray-100">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-2">
              <div>
                <span className="text-gray-500">Requester:</span>{' '}
                <span className="font-medium text-gray-800">{requestDetail.requester_name || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500">Type:</span>{' '}
                <span className="font-medium text-gray-800">
                  {({'PEER_MID_LEVEL':'Peer Networking','PEER_SENIOR':'Peer Networking (Senior)','MENTORSHIP_UP':'Seek Mentorship','MENTORSHIP_HIGH':'Offer Mentorship','BOARD_PATHWAY':'Board Pathway','HBCU_MENTORSHIP':'HBCU Mentorship'})[requestDetail.request_type] || (requestDetail.request_type || '').replace(/_/g, ' ')}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Target:</span>{' '}
                <span className="font-medium text-gray-800">{requestDetail.target_name || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500">Priority:</span>{' '}
                <span className="font-medium text-gray-800">
                  {requestDetail.priority_score != null ? Number(requestDetail.priority_score).toFixed(2) : '—'}
                </span>
              </div>
            </div>
            {requestDetail.request_payload?.topics_of_interest?.length > 0 && (
              <div>
                <span className="text-gray-500">Topics:</span>{' '}
                <span className="text-gray-800">{requestDetail.request_payload.topics_of_interest.join(', ')}</span>
              </div>
            )}
            {requestDetail.request_payload?.desired_expertise?.length > 0 && (
              <div>
                <span className="text-gray-500">Desired Expertise:</span>{' '}
                <span className="text-gray-800">{requestDetail.request_payload.desired_expertise.join(', ')}</span>
              </div>
            )}
            {requestDetail.request_payload?.request_context && (
              <div>
                <span className="text-gray-500">Context:</span>{' '}
                <span className="text-gray-800">{requestDetail.request_payload.request_context}</span>
              </div>
            )}
            {requestDetail.staff_notes && (
              <div>
                <span className="text-gray-500">Staff Notes:</span>{' '}
                <span className="text-gray-800">{requestDetail.staff_notes}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}
      {successMsg && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="ml-2 text-green-500 hover:text-green-700">&times;</button>
        </div>
      )}

      {consentLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          <span className="ml-2 text-sm text-gray-500">Loading consent details...</span>
        </div>
      ) : (
        <>
          {/* Consent History */}
          {consentData?.items?.length > 0 && (
            <div className="space-y-3">
              {consentData.items.map((consent, idx) => {
                const expiry = getExpiryInfo(consent);
                const isPending = consent.consent_status === 'pending';
                const isGranted = consent.consent_status === 'granted';
                const isDeclined = consent.consent_status === 'declined';

                return (
                  <div key={consent.id || idx} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <FiShield className={`w-4 h-4 ${isGranted ? 'text-green-500' : isDeclined ? 'text-red-500' : 'text-amber-500'}`} />
                        <span className="text-sm font-medium text-gray-800">Consent Request</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          isGranted ? 'bg-green-100 text-green-700' :
                          isDeclined ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {consent.consent_status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </div>
                      {consent.consent_requested_at && (
                        <span className="text-xs text-gray-400">
                          Sent {new Date(consent.consent_requested_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {/* Consent details */}
                    <div className="space-y-2 text-sm">
                      {consent.teaser_shown && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase">Teaser Shown</span>
                          <p className="text-gray-700 mt-0.5">{consent.teaser_shown}</p>
                        </div>
                      )}
                      {consent.privacy_level && (
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="text-xs text-gray-500">Privacy Level: </span>
                            <span className="text-gray-700">{consent.privacy_level === 1 ? 'Minimal' : 'Detailed'}</span>
                          </div>
                        </div>
                      )}
                      {consent.consent_token && isPending && (
                        <div className="flex items-center gap-2 bg-gray-50 rounded p-2">
                          <span className="text-xs text-gray-500">Token:</span>
                          <code className="text-xs text-gray-600 flex-1 truncate">{consent.consent_token}</code>
                          <button
                            onClick={() => copyToClipboard(consent.consent_token)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Copy token"
                          >
                            <FiCopy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      {expiry && isPending && (
                        <div className={`flex items-center gap-1 ${expiry.color}`}>
                          <FiClock className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium">{expiry.text}</span>
                        </div>
                      )}
                      {consent.declined_reason && (
                        <div className="bg-red-50 rounded p-2">
                          <span className="text-xs text-red-500 uppercase">Decline Reason</span>
                          <p className="text-sm text-red-700 mt-0.5">{consent.declined_reason}</p>
                        </div>
                      )}
                    </div>

                    {/* Manual response actions for pending consent */}
                    {isPending && idx === 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500 uppercase mb-2">Record Manual Response</p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => recordConsentResponse('granted')}
                            disabled={respondLoading}
                            className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            <FiCheckCircle className="w-3.5 h-3.5" /> Grant
                          </button>
                          <button
                            onClick={() => setShowDeclineReason(true)}
                            disabled={respondLoading}
                            className="px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 flex items-center gap-1"
                          >
                            <FiXCircle className="w-3.5 h-3.5" /> Decline
                          </button>
                          <button
                            onClick={() => recordConsentResponse('expired')}
                            disabled={respondLoading}
                            className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1"
                          >
                            <FiClock className="w-3.5 h-3.5" /> Mark Expired
                          </button>
                        </div>

                        {/* Decline reason */}
                        {showDeclineReason && (
                          <div className="mt-3 space-y-2">
                            <textarea
                              value={declinedReason}
                              onChange={(e) => setDeclinedReason(e.target.value)}
                              rows={2}
                              className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-red-500 focus:border-red-500"
                              placeholder="Reason for declining..."
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => recordConsentResponse('declined')}
                                disabled={respondLoading}
                                className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                              >
                                {respondLoading ? 'Saving...' : 'Confirm Decline'}
                              </button>
                              <button
                                onClick={() => { setShowDeclineReason(false); setDeclinedReason(''); }}
                                className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Create Consent Request */}
          {!hasActiveConsent && !consentGranted && (
            <div className="border border-gray-200 rounded-lg p-4">
              {!showCreateForm ? (
                <div className="text-center py-2">
                  <p className="text-sm text-gray-500 mb-3">
                    {consentData?.items?.length > 0
                      ? 'Previous consent was declined or expired. You can create a new request.'
                      : 'No consent request has been created yet.'}
                  </p>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Create Consent Request
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs font-medium text-gray-500 uppercase">New Consent Request</p>

                  {/* Privacy Level */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Privacy Level</label>
                    <div className="flex gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="privacyLevel"
                          value={1}
                          checked={privacyLevel === 1}
                          onChange={() => setPrivacyLevel(1)}
                          className="text-indigo-600"
                        />
                        <span className="text-sm text-gray-700">Level 1 - Minimal</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="privacyLevel"
                          value={2}
                          checked={privacyLevel === 2}
                          onChange={() => setPrivacyLevel(2)}
                          className="text-indigo-600"
                        />
                        <span className="text-sm text-gray-700">Level 2 - Detailed</span>
                      </label>
                    </div>
                  </div>

                  {/* Requester Teaser */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-600">Requester Teaser</label>
                      <button
                        onClick={generateTeaser}
                        disabled={teaserLoading}
                        className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50"
                      >
                        <FiRefreshCw className={`w-3 h-3 ${teaserLoading ? 'animate-spin' : ''}`} />
                        {teaserLoading ? 'Generating...' : 'Auto-generate'}
                      </button>
                    </div>
                    <textarea
                      value={requesterTeaser}
                      onChange={(e) => setRequesterTeaser(e.target.value)}
                      rows={3}
                      className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Describe the requester in a way that protects their identity..."
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      This teaser will be shown to the target. It should describe the requester without revealing their identity.
                    </p>
                  </div>

                  {/* Custom Message */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Custom Message (Optional)</label>
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      rows={2}
                      className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Optional personal message to include with the consent request..."
                    />
                  </div>

                  {/* Test Mode */}
                  <div className={`flex items-center gap-2 p-3 rounded-lg border ${testMode ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
                    <input
                      type="checkbox"
                      id="consentTestMode"
                      checked={testMode}
                      onChange={(e) => setTestMode(e.target.checked)}
                      className="h-4 w-4 text-amber-600 rounded border-gray-300"
                    />
                    <label htmlFor="consentTestMode" className="text-sm text-gray-700 cursor-pointer">
                      <span className="font-medium">Test mode</span>
                      <span className="text-xs text-gray-500 ml-1">— sends to test email instead of the real target</span>
                    </label>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={createConsentRequest}
                      disabled={createLoading || !requesterTeaser.trim()}
                      className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
                        testMode
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      {createLoading ? 'Sending...' : testMode ? 'Send Test Consent Request' : 'Send Consent Request'}
                    </button>
                    <button
                      onClick={() => { setShowCreateForm(false); setRequesterTeaser(''); setCustomMessage(''); setTestMode(false); }}
                      className="px-4 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Consent Granted - Proceed guidance */}
          {consentGranted && (
            <div className="border border-green-200 bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FiCheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">Consent Granted</span>
              </div>
              <p className="text-sm text-green-700 mb-3">
                The target has consented to the introduction. You can now proceed to schedule a session.
              </p>
              {requestDetail.valid_transitions?.includes('CONSENTED') && (
                <button
                  onClick={() => transitionRequest('CONSENTED')}
                  disabled={transitionLoading}
                  className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {transitionLoading ? 'Transitioning...' : 'Proceed to Schedule'}
                </button>
              )}
            </div>
          )}

          {/* Transition buttons */}
          {requestDetail.valid_transitions?.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 uppercase mb-3">Other Actions</p>
              <div className="flex flex-wrap gap-2">
                {requestDetail.valid_transitions?.includes('WITHDRAWN') && (
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to withdraw this request?')) {
                        transitionRequest('WITHDRAWN');
                      }
                    }}
                    disabled={transitionLoading}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    Withdraw
                  </button>
                )}
                {requestDetail.valid_transitions?.includes('DECLINED') && (
                  <button
                    onClick={() => transitionRequest('DECLINED')}
                    disabled={transitionLoading}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                  >
                    Decline
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConsentPanel;
